import React, { useCallback, useState } from "react";
import Button, { ButtonProps } from "./Button";
import { Modal, Stack, Typography } from "@mui/material";
import TextField from "./TextField";

interface Props {
    children?: string;
    onCancel?: () => void;
    onSave?: (
        text: string,
        close: () => void,
        setLoading: (val: boolean) => void
    ) => void;
    title?: string;
    placeholder?: string;
    cancelText?: string;
    saveText?: string;
    note?: string;
    button: ButtonProps;
}

const TextModalButton = (props: Props) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const { children, button, title, placeholder, cancelText, saveText, note } =
        props;

    const onCancel = useCallback(() => {
        setOpen(false);
        props.onCancel?.();
    }, [props]);

    const onSave = useCallback(
        (text: string) => {
            props.onSave?.(text, () => setOpen(false), setLoading);
        },
        [props]
    );

    return (
        <>
            <Button
                {...button}
                children={children}
                onClick={() => setOpen(true)}
            />
            <Modal open={open} onClose={() => {}}>
                <TextModal
                    onCancel={onCancel}
                    onSave={onSave}
                    title={title}
                    placeholder={placeholder}
                    cancelText={cancelText}
                    saveText={saveText}
                    loading={loading}
                    note={note}
                />
            </Modal>
        </>
    );
};

const TextModal = (props: {
    onCancel: () => void;
    onSave: (text: string) => void;
    title?: string;
    placeholder?: string;
    cancelText?: string;
    saveText?: string;
    loading?: boolean;
    note?: string;
}) => {
    const {
        onCancel,
        onSave,
        title,
        placeholder,
        cancelText,
        saveText,
        loading,
    } = props;

    const [text, setText] = useState(props.note ?? "");

    return (
        <Stack
            position="absolute"
            top="50%"
            left="50%"
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={2}
            width="85%"
            padding={2}
            bgcolor="white"
            borderRadius="14px"
            boxShadow="0px 4px 17px 0px #0000000A"
            sx={{
                transform: "translate(-50%, -50%)",
            }}
        >
            <Typography variant="h6" component="h2">
                {title ?? "Note"}
            </Typography>
            <TextField
                value={text}
                onChange={(e) => setText(e.currentTarget.value)}
                placeholder={placeholder}
                fullWidth
                multiline
                variant="contained"
                sx={{
                    bgcolor: "transparent",
                    padding: 2,
                    fontWeight: 400,
                    fontSize: "15px",
                    lineHeight: "24.9px",
                }}
            />
            <Stack direction="row" width="100%" spacing={2}>
                <Button
                    variant="medium"
                    fullWidth
                    onClick={onCancel}
                    disabled={loading}
                >
                    {cancelText ?? "Cancel"}
                </Button>
                <Button
                    variant="medium"
                    fullWidth
                    onClick={() => onSave(text)}
                    disabled={loading}
                >
                    {saveText ?? "Save"}
                </Button>
            </Stack>
        </Stack>
    );
};

export default TextModalButton;
